/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, October 21st 2022, 2:40:34 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import {
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  EmailShareButton,
  EmailIcon,
  TelegramShareButton,
  TelegramIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from "react-share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import IconButton from "@mui/material/IconButton";
import Grid, { GridSize } from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { FormConfiguration, ProcessDefinition } from "lib/schemas";
import { useCopyTextToClipBoard } from "lib/utils";
import { StyledOSDialog, useToggleState, EmphasisText } from "ui-widgets";

interface ShareSubmissionLinkProps {
  processDefinitionId: ProcessDefinition["id"];
  processDefinitionSiteURL: ProcessDefinition["site_url"];
  formTitle: FormConfiguration["title"];
  formSlug: FormConfiguration["slug"];
  formType: FormConfiguration["form_type"];
  children: (args: { handleOpen: () => void }) => React.ReactNode;
}

export const ShareFormSubmissionLink = (props: ShareSubmissionLinkProps) => {
  const { copied, open, shareButtons, socialIconGridItemSize, handleClose, handleOpen } = useShareSubmissionLink(props);

  const { formTitle, children } = props;

  if (!Boolean(shareButtons)) return null;

  return (
    <>
      {children({ handleOpen })}

      <StyledOSDialog
        open={open}
        onClose={handleClose}
        popUpTitle="Share Submission Link"
        popUpIcon={<ShareIcon color="primary" />}
        maxWidth="xs"
      >
        <Snackbar open={copied} autoHideDuration={6000} message="Submission Link Copied" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography>
              Share the submission link
              {formTitle && (
                <>
                  {" "}
                  for <EmphasisText>{formTitle}</EmphasisText>
                </>
              )}{" "}
              on email, social media or copy the link.
            </Typography>
          </Grid>
          {shareButtons.map((shareButton, index) => (
            <Grid item xs={socialIconGridItemSize} key={`${index}`}>
              <Tooltip title={<Typography>{shareButton.toolTip}</Typography>}>
                <div>{shareButton.button}</div>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </StyledOSDialog>
    </>
  );
};

const useShareSubmissionLink = ({
  formSlug,
  formType,
  processDefinitionId,
  processDefinitionSiteURL,
}: ShareSubmissionLinkProps) => {
  const { handleClose, handleOpen, open } = useToggleState();

  const { copied, copyTextToClipBoard } = useCopyTextToClipBoard();

  if (!Boolean(processDefinitionId) || !Boolean(processDefinitionSiteURL) || !Boolean(formSlug)) return {};

  let submissionURL;

  switch (formType) {
    case "application_form":
      submissionURL = `${processDefinitionSiteURL}/forms/${formSlug}/process/${processDefinitionId}`;
      break;
    case "registration_form":
      submissionURL = `${processDefinitionSiteURL}/accounts/sub-profiles/register/${processDefinitionId}/${formSlug}`;
      break;
  }

  const socialIconSize = 35;

  const socialIconRound = true;

  const socialIconGridItemSize: GridSize = 3;

  const shareButtons: Record<"button" | "toolTip", React.ReactNode>[] = [
    {
      button: (
        <IconButton onClick={() => copyTextToClipBoard(submissionURL)}>
          <ContentCopyIcon color="secondary" />
        </IconButton>
      ),
      toolTip: "Copy Link",
    },

    {
      button: (
        <EmailShareButton url={submissionURL}>
          <EmailIcon size={socialIconSize} round={socialIconRound} />
        </EmailShareButton>
      ),
      toolTip: "Share on Email",
    },
    {
      button: (
        <LinkedinShareButton url={submissionURL}>
          <LinkedinIcon size={socialIconSize} round={socialIconRound} />
        </LinkedinShareButton>
      ),
      toolTip: "Share on LinkedIn",
    },
    {
      button: (
        <WhatsappShareButton url={submissionURL}>
          <WhatsappIcon size={socialIconSize} round={socialIconRound} />
        </WhatsappShareButton>
      ),
      toolTip: "Share on WhatsApp",
    },
    {
      button: (
        <TelegramShareButton url={submissionURL}>
          <TelegramIcon size={socialIconSize} round={socialIconRound} />
        </TelegramShareButton>
      ),
      toolTip: "Share on Telegram",
    },
    {
      button: (
        <TwitterShareButton url={submissionURL}>
          <TwitterIcon size={socialIconSize} round={socialIconRound} />
        </TwitterShareButton>
      ),
      toolTip: "Share on Twitter",
    },
    {
      button: (
        <FacebookShareButton url={submissionURL}>
          <FacebookIcon size={socialIconSize} round={socialIconRound} />
        </FacebookShareButton>
      ),
      toolTip: "Share on Facebook",
    },
  ];

  return { copied, open, shareButtons, socialIconGridItemSize, handleClose, handleOpen };
};
